import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, Modal, ScrollView, TextInput } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface CommentsModalProps {
    visible: boolean;
    onClose: () => void;
    title: string;
    comments: Array<{
        id: string;
        text: string;
        timestamp: string;
        author: string;
    }>;
    onAddComment: (text: string) => void;
    canAdd?: boolean; // Whether user can add new comments
}

const CommentsModal: React.FC<CommentsModalProps> = ({
    visible,
    onClose,
    title,
    comments,
    onAddComment,
    canAdd = true
}) => {
    const [newComment, setNewComment] = useState('');

    const handleAddComment = () => {
        if (newComment.trim()) {
            onAddComment(newComment.trim());
            setNewComment('');
        }
    };

    const formatTimestamp = (timestamp: string) => {
        return new Date(timestamp).toLocaleString();
    };

    return (
        <Modal visible={visible} animationType="slide" transparent={true}>
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={styles.headerLeft}>
                            <MaterialCommunityIcons name="comment-text" size={24} color="#2C67D6" />
                            <Text style={styles.modalTitle}>{title}</Text>
                        </View>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={24} color="#666" />
                        </Pressable>
                    </View>

                    {/* Comments List */}
                    <ScrollView style={styles.commentsContainer} showsVerticalScrollIndicator={false}>
                        {comments.length > 0 ? (
                            comments.map((comment) => (
                                <View key={comment.id} style={styles.commentItem}>
                                    <View style={styles.commentHeader}>
                                        <Text style={styles.commentAuthor}>{comment.author}</Text>
                                        <Text style={styles.commentTimestamp}>
                                            {formatTimestamp(comment.timestamp)}
                                        </Text>
                                    </View>
                                    <Text style={styles.commentText}>{comment.text}</Text>
                                </View>
                            ))
                        ) : (
                            <View style={styles.emptyState}>
                                <MaterialCommunityIcons name="comment-outline" size={48} color="#ccc" />
                                <Text style={styles.emptyStateText}>No comments yet</Text>
                            </View>
                        )}
                    </ScrollView>

                    {/* Add New Comment - Only show for users with permission */}
                    {canAdd && (
                        <View style={styles.addCommentContainer}>
                            <TextInput
                                style={styles.commentInput}
                                placeholder="Add a comment..."
                                value={newComment}
                                onChangeText={setNewComment}
                                multiline={true}
                                numberOfLines={3}
                            />
                            <Pressable
                                style={[styles.addButton, !newComment.trim() && styles.addButtonDisabled]}
                                onPress={handleAddComment}
                                disabled={!newComment.trim()}
                            >
                                <MaterialCommunityIcons name="send" size={20} color="white" />
                                <Text style={styles.addButtonText}>Add Comment</Text>
                            </Pressable>
                        </View>
                    )}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        width: '90%',
        maxWidth: 500,
        height: '70%',
        backgroundColor: 'white',
        borderRadius: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 16,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
        marginLeft: 8,
    },
    closeButton: {
        padding: 4,
    },
    commentsContainer: {
        flex: 1,
        padding: 20,
    },
    commentItem: {
        backgroundColor: '#f8f9fa',
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    commentHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    commentAuthor: {
        fontSize: 14,
        fontWeight: '600',
        color: '#2C67D6',
    },
    commentTimestamp: {
        fontSize: 12,
        color: '#666',
    },
    commentText: {
        fontSize: 14,
        color: '#333',
        lineHeight: 20,
    },
    emptyState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
    },
    emptyStateText: {
        fontSize: 16,
        color: '#999',
        marginTop: 16,
        textAlign: 'center',
    },
    addCommentContainer: {
        padding: 20,
        borderTopWidth: 1,
        borderTopColor: '#e9ecef',
    },
    commentInput: {
        borderWidth: 1,
        borderColor: '#e9ecef',
        borderRadius: 12,
        padding: 12,
        fontSize: 14,
        color: '#333',
        backgroundColor: 'white',
        marginBottom: 12,
        minHeight: 60,
        textAlignVertical: 'top',
    },
    addButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#2C67D6',
        borderRadius: 8,
        padding: 12,
    },
    addButtonDisabled: {
        backgroundColor: '#ccc',
    },
    addButtonText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '600',
        marginLeft: 6,
    },
});

export default CommentsModal; 